import { Alert, Card, Col, ConfigProvider, DatePicker, Divider, Form, Row, Statistic, Typography, Select } from "antd";
import SDIModal from "../../../../components/ui/SDIModal";
import PropTypes from "prop-types";
import frFR from "antd/locale/fr_FR";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AvenantAgencySelector from "./AvenantAgencySelector";
import { toast } from "react-toastify";
import axiosInstance from "../../../../variables/api/axiosInstance";

const { Text, Title } = Typography;
const { Option } = Select;

const Prorogation = (props) => {
  const [modalProrogation, setModalProrogation] = useState(props.open);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  // États pour les données du contrat
  const [contratActuel, setContratActuel] = useState(null);
  const [dateEffetProrogation, setDateEffetProrogation] = useState(null);
  const [dureeProrogation, setDureeProrogation] = useState(6); // Par défaut 6 mois
  const [dateEcheanceProrogation, setDateEcheanceProrogation] = useState(null);
  const [preview, setPreview] = useState(null);
  const [fraisAccessoires, setFraisAccessoires] = useState(0);
  const [joursEcoules, setJoursEcoules] = useState(0);

  useEffect(() => {
    setModalProrogation(props.open);
    if (props.open && props?.contrat?.codeId) {
      console.log("Contrat pour prorogation:", props?.contrat);
      setContratActuel(props?.contrat);

      // Calculer la date d'effet par défaut (jour suivant l'expiration du premier 6 mois)
      const dateEcheancePremierSixMois = dayjs(props?.contrat?.dateEcheance);
      const dateEffetDefaut = dateEcheancePremierSixMois.add(1, 'day');
      setDateEffetProrogation(dateEffetDefaut);

      setDureeProrogation(6);
      // Default 6 mois
      const nouvelleEcheance = dateEffetDefaut.add(6, 'month');
      setDateEcheanceProrogation(nouvelleEcheance);

      // Calculer les jours écoulés depuis l'expiration
      const joursEcoulesCalcules = dayjs().diff(dateEcheancePremierSixMois, 'day');
      setJoursEcoules(Math.max(0, joursEcoulesCalcules));

      // Récupérer les frais accessoires pour la prorogation
      axiosInstance.get(`/api/avenant/accessoires/PR/${props?.contrat?.engins[0]?.categorie?.codeCategorie}`)
        .then(response => {
          setFraisAccessoires(response.data);
        })
        .catch(error => {
          console.error("Erreur lors de la récupération des frais accessoires:", error);
        });
    }
  }, [props.open, props?.contrat?.codeId]);

  const { CalendarOutlined, DollarOutlined, CheckCircleOutlined, WarningOutlined } = require('@ant-design/icons');

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(value);
  };

  const handleDateEffetChange = (date) => {
    setDateEffetProrogation(date);

    // Recalculer la date d'échéance selon la durée
    // if (date && dureeProrogation) {
    //   const nouvelleEcheance = date.add(dureeProrogation, 'month');
    //   setDateEcheanceProrogation(nouvelleEcheance);
    // }
  };

  const handleDureeChange = (duree) => {
    setDureeProrogation(duree);

    // Recalculer la date d'échéance
    // if (dateEffetProrogation && duree) {
    //   const nouvelleEcheance = dateEffetProrogation.add(duree, 'month');
    //   setDateEcheanceProrogation(nouvelleEcheance);
    // }
  };

  const calculatePreview = async () => {
    if (!contratActuel || !dateEffetProrogation || !dureeProrogation) return;

    try {
      const payload = {
        contratCodeId: contratActuel.codeId,
        dateEffet: dateEffetProrogation.format('YYYY-MM-DD'),
        dureeEnMois: dureeProrogation,
        dateEcheance: dateEcheanceProrogation?.format('YYYY-MM-DD')
      };

      const response = await axiosInstance.put('/api/contrat/preview-prorogation', JSON.stringify(payload));
      console.log("Preview prorogation:", response.data);
      setPreview(response.data);
    } catch (error) {
      console.error("Erreur lors du calcul du preview:", error);
    }
  };

  useEffect(() => {
    if (dateEffetProrogation && dureeProrogation && dateEcheanceProrogation) {
      calculatePreview();
    }
  }, [dateEffetProrogation, dureeProrogation, dateEcheanceProrogation, contratActuel]);

  const navigate = useNavigate();

  const enterLoading = () => setLoading(true);
  const outLoading = () => setLoading(false);

  const handleProrogationSubmit = async () => {
    try {
      await form.validateFields();

      // Vérifications d'éligibilité
      if (!isContratEligible()) {
        toast.error("Ce contrat n'est pas éligible à la prorogation");
        return;
      }

      if (joursEcoules > 30) {
        toast.error("La prorogation ne peut plus être effectuée (plus de 30 jours après expiration)");
        return;
      }

      enterLoading();

      const payload = {
        contratCodeId: contratActuel.codeId,
        dateEffet: dateEffetProrogation.format('YYYY-MM-DD'),
        dateEcheance: dateEcheanceProrogation.format('YYYY-MM-DD'),
        dureeEnMois: dureeProrogation,
        primeNette: preview?.primeNette || 0,
        primeNetteFractionnee: preview?.primeNetteFractionnee || 0,
        accessoires: fraisAccessoires,
        taxes: preview?.taxes || 0,
        primeTotale: preview?.primeTotale || 0,
        joursEcoules
      };

      console.log("Payload prorogation:", payload);

      const response = await axiosInstance.put(
        `/api/contrat/prorogation`,
        JSON.stringify(payload)
      );

      toast.success("Prorogation du contrat effectuée avec succès!");
      setModalProrogation(false);
      props.update(false);
      navigate(`/details-contrat/${response.data.codeId}`, { replace: true });
      window.location.reload();

    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la prorogation du contrat");
    } finally {
      outLoading();
    }
  };

  const handleCancel = () => {
    setModalProrogation(false);
    props.update(false);
  };

  const isContratEligible = () => {
    return contratActuel?.fractionnement?.nombre > 1 &&
      contratActuel?.avenant?.code !== 'SP' &&
      joursEcoules <= 30 &&
      (contratActuel?.avenant?.code === 'RN');
  };

  const getDureeRestanteMaximale = () => {
    // La durée cumulée ne peut pas excéder 12 mois
    const dureeActuelle = contratActuel?.dureeEnMois || 6;
    return 12 - dureeActuelle;
  };

  const getDateLimiteEffet = () => {
    if (!contratActuel?.dateEcheance) return null;
    const dateExpiration = dayjs(contratActuel.dateEcheance);
    return dateExpiration.add(30, 'day');
  };

  return (
    <SDIModal
      title="PROROGATION DE CONTRAT"
      centered
      confirmLoading={loading}
      open={modalProrogation}
      onOk={handleProrogationSubmit}
      onCancel={handleCancel}
      width={1000}
      zIndex={1940}
      okText="Établir la prorogation"
      cancelText="Annuler"
    >
      <ConfigProvider locale={frFR}>
        <div className="prorogation-content">
          <AvenantAgencySelector
            organismeCode={props?.contrat?.organisme?.code}
            defaultAgence={props?.contrat?.producteur?.agence || props?.contrat?.intermediaire?.agence}
          />

          {/* Informations sur le contrat */}
          {contratActuel && (
            <Card className="mb-4" size="small" title="Informations du contrat à proroger">
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic
                    title="Numéro de police"
                    value={contratActuel.numeroPoliceP || contratActuel.numeroPoliceM}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Fractionnement"
                    value={`${contratActuel.fractionnement?.moisMin || 'N/A'} mois`}
                    valueStyle={{ color: (contratActuel.fractionnement?.nombre > 1) ? '#3f8600' : '#cf1322' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Échéance actuelle"
                    value={dayjs(contratActuel.dateEcheance).format('DD/MM/YYYY')}
                    prefix={<CalendarOutlined />}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Jours écoulés"
                    value={`${joursEcoules} jours`}
                    valueStyle={{ color: joursEcoules <= 30 ? '#3f8600' : '#cf1322' }}
                  />
                </Col>
              </Row>
            </Card>
          )}

          {/* Conditions d'éligibilité */}
          {!isContratEligible() ? (
            <Alert
              message="Contrat non éligible à la prorogation"
              description={
                contratActuel?.fractionnement?.nombre <= 1
                  ? "La prorogation n'est autorisée que pour les contrats fractionnés."
                  : joursEcoules > 30
                    ? "La prorogation ne peut être effectuée que dans les 30 jours suivant l'expiration."
                    : contratActuel?.avenant?.code === 'SP'
                      ? "Un contrat suspendu ne peut pas être prorogé."
                      : "Le contrat doit justifier d'un avenant de renouvellement de 6 mois en cours ou expiré depuis maximum 30 jours."
              }
              type="error"
              icon={<WarningOutlined />}
              showIcon
              className="mb-4"
            />
          ) : (
            <Alert
              message="Contrat éligible à la prorogation"
              description="Ce contrat remplit les conditions pour être prorogé (deuxième période de 6 mois)."
              type="success"
              icon={<CheckCircleOutlined />}
              showIcon
              className="mb-4"
            />
          )}

          {/* Informations sur la prorogation */}
          {/* <Card className="mb-4" size="small" title="Règles de prorogation">
            <div className="info-section">
              <div className="info-item">
                <InfoCircleOutlined className="info-icon info" />
                <Text>
                  <Text strong>Code avenant :</Text> PR2024026 (renouvellement du deuxième six mois)
                </Text>
              </div>
              <div className="info-item">
                <ClockCircleOutlined className="info-icon warning" />
                <Text>
                  <Text strong>Durée maximale :</Text> La durée cumulée ne peut excéder 12 mois
                </Text>
              </div>
              <div className="info-item">
                <CalendarOutlined className="info-icon success" />
                <Text>
                  <Text strong>Date d'effet :</Text> Par défaut le jour suivant l'expiration,
                  ou dans les 30 jours suivants
                </Text>
              </div>
            </div>
          </Card> */}

          <Form form={form} layout="vertical">
            {/* Date d'effet de la prorogation */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Date d'effet de la prorogation"
                  name="dateEffetProrogation"
                  rules={[{ required: true, message: 'Veuillez sélectionner une date' }]}
                  initialValue={dateEffetProrogation}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    value={dateEffetProrogation}
                    onChange={handleDateEffetChange}
                    disabledDate={(current) => {
                      if (!contratActuel?.dateEcheance) return false;
                      const dateExpiration = dayjs(contratActuel.dateEcheance);
                      const dateLimite = dateExpiration.add(30, 'day');
                      // Date doit être entre le jour suivant l'expiration et 30 jours après
                      return current && (
                        current < dateExpiration.add(1, 'day').startOf('day') ||
                        current > dateLimite.endOf('day')
                      );
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Durée de prorogation"
                  name="dureeProrogation"
                  rules={[{ required: true, message: 'Veuillez sélectionner une durée' }]}
                  initialValue={dureeProrogation}
                >
                  <Select
                    value={dureeProrogation}
                    onChange={handleDureeChange}
                    style={{ width: '100%' }}
                  >
                    <Option value={6}>6 mois (deuxième période standard)</Option>
                    {getDureeRestanteMaximale() > 6 && (
                      <>
                        <Option value={3}>3 mois</Option>
                        <Option value={4}>4 mois</Option>
                        <Option value={5}>5 mois</Option>
                      </>
                    )}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* Informations calculées */}
            {dateEffetProrogation && dateEcheanceProrogation && (
              <Card className="mb-4" size="small" title="Informations calculées">
                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic
                      title="Date d'échéance de la prorogation"
                      value={dateEcheanceProrogation.format('DD/MM/YYYY')}
                      valueStyle={{ color: '#1890ff' }}
                      prefix={<CalendarOutlined />}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Durée totale cumulée"
                      value={`${(contratActuel?.fractionnement?.moisMin || 6) + dureeProrogation} mois`}
                      valueStyle={{
                        color: ((contratActuel?.fractionnement?.moisMin || 6) + dureeProrogation) <= 12 ? '#3f8600' : '#cf1322'
                      }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Type d'avenant"
                      value={`PR${new Date().getFullYear()}026`}
                      valueStyle={{ color: '#722ed1' }}
                    />
                  </Col>
                </Row>
              </Card>
            )}

            {/* Récapitulatif financier */}
            {preview && (
              <Card className="cost-summary" size="small">
                <Title level={5}>
                  <DollarOutlined className="mr-2" />
                  Récapitulatif
                </Title>
                <div className="cost-breakdown">
                  <Row justify="space-between" className="cost-line">
                    <Col>
                      <Text>Prime nette annuelle (avec Bonus-Malus et réductions):</Text>
                    </Col>
                    <Col>
                      <Text strong>{formatCurrency(preview.primeNette || 0)}</Text>
                    </Col>
                  </Row>

                  <Row justify="space-between" className="cost-line">
                    <Col>
                      <Text>Frais accessoires:</Text>
                    </Col>
                    <Col>
                      <Text>{formatCurrency(fraisAccessoires)}</Text>
                    </Col>
                  </Row>

                  <Row justify="space-between" className="cost-line">
                    <Col>
                      <Text>Prime nette fractionnée (Prime nette annuelle + accessoires):</Text>
                    </Col>
                    <Col>
                      <Text strong>{formatCurrency(preview.primeNetteFractionnee || 0)}</Text>
                    </Col>
                  </Row>


                  <Row justify="space-between" className="cost-line">
                    <Col>
                      <Text>Taxes (6%):</Text>
                    </Col>
                    <Col>
                      <Text>{formatCurrency(preview.taxes || 0)}</Text>
                    </Col>
                  </Row>

                  <Divider className="my-2" />

                  <Row justify="space-between" className="cost-line total-cost">
                    <Col>
                      <Text strong className="text-lg">PRIME TOTALE:</Text>
                    </Col>
                    <Col>
                      <Text strong className="text-lg text-blue-600">
                        {formatCurrency(preview.primeTotale || 0)}
                      </Text>
                    </Col>
                  </Row>
                </div>
              </Card>
            )}
          </Form>
        </div>
      </ConfigProvider>

      <style jsx>
        {`
                    .prorogation-content {
                        max-height: 70vh;
                        overflow-y: auto;
                    }
                    
                    .info-section {
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                    }
                    
                    .info-item {
                        display: flex;
                        align-items: flex-start;
                        gap: 8px;
                    }
                    
                    .info-icon {
                        margin-top: 2px;
                        font-size: 16px;
                    }
                    
                    .info-icon.warning {
                        color: #faad14;
                    }
                    
                    .info-icon.info {
                        color: #1890ff;
                    }
                    
                    .info-icon.success {
                        color: #52c41a;
                    }
                    
                    .cost-summary {
                        background-color: #f8f9fa;
                        border: 1px solid #e9ecef;
                    }
                    
                    .cost-breakdown .cost-line {
                        margin-bottom: 8px;
                    }
                    
                    .cost-breakdown .total-cost {
                        background-color: #e6f7ff;
                        padding: 8px;
                        border-radius: 4px;
                        margin-top: 8px;
                    }
                    
                    .mr-2 { margin-right: 8px; }
                    .mb-4 { margin-bottom: 16px; }
                    .my-2 { margin: 8px 0; }
                    .text-lg { font-size: 16px; }
                    .text-blue-600 { color: #1890ff; }
                `}
      </style>
    </SDIModal>
  );
};

Prorogation.propTypes = {
  open: PropTypes.bool,
  update: PropTypes.func,
  contrat: PropTypes.object
};

export default Prorogation;
